import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import cn from 'classnames';

import { WEEKDAYS_RU } from '../data/constants';
import { usePeriodPicker } from '../hooks/usePeriodPicker';

import type { Dayjs } from 'dayjs';
import type { FC, ReactNode } from 'react';

import './PeriodPicker.scss';

export interface PeriodPickerPreset {
  readonly id: string;
  readonly label: string;
}

interface PeriodPickerProps {
  presets: readonly PeriodPickerPreset[];
  activePresetId: string | null;
  viewMonth: Dayjs;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  isSelecting: boolean;
  previewStart: Dayjs | null;
  previewEnd: Dayjs | null;
  // Extra column to the right of the calendar (e.g. task all-day and time).
  aside?: ReactNode;
  hint?: ReactNode;
  onPresetSelect(id: string): void;
  onDayClick(day: Dayjs): void;
  onDayHover(day: Dayjs | null): void;
  onViewMonthChange(month: Dayjs): void;
  isSaveDisabled?: boolean;
  // Renders the «Сбросить период» action when the period is optional.
  onReset?(): void;
  // Renders «Сохранить»: the host applies the picked period and closes.
  onSave?(): void;
}

// Period picker panel shared by the leads registry period filter and the task
// date chips: presets, a Monday-first calendar and optional aside/hint/reset.
// Selection state lives in the host, so each host keeps its own apply rules.
export const PeriodPicker: FC<PeriodPickerProps> = ({
  presets,
  activePresetId,
  viewMonth,
  startDate,
  endDate,
  isSelecting,
  previewStart,
  previewEnd,
  aside,
  hint,
  isSaveDisabled = false,
  onPresetSelect,
  onDayClick,
  onDayHover,
  onViewMonthChange,
  onReset,
  onSave,
}) => {
  const {
    grid,
    monthLabel,
    getDayModifier,
    isToday,
    onPrevMonth,
    onNextMonth,
  } = usePeriodPicker({
    viewMonth,
    startDate,
    endDate,
    isSelecting,
    previewStart,
    previewEnd,
    onViewMonthChange,
  });

  return (
    <div className="PeriodPicker">
      <div className="PeriodPicker__body">
        <div className="PeriodPicker__presets">
          {presets.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={cn('PeriodPicker__preset', {
                'PeriodPicker__preset--active': id === activePresetId,
              })}
              onClick={() => onPresetSelect(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="PeriodPicker__divider" />

        <div className="PeriodPicker__calendar">
          <div className="PeriodPicker__calendar-header">
            <button
              type="button"
              className="PeriodPicker__nav-btn"
              aria-label="Предыдущий месяц"
              onClick={onPrevMonth}
            >
              <LeftOutlined />
            </button>
            <span className="PeriodPicker__month-label">{monthLabel}</span>
            <button
              type="button"
              className="PeriodPicker__nav-btn"
              aria-label="Следующий месяц"
              onClick={onNextMonth}
            >
              <RightOutlined />
            </button>
          </div>

          <div className="PeriodPicker__weekdays">
            {WEEKDAYS_RU.map((weekday) => (
              <span key={weekday} className="PeriodPicker__weekday">
                {weekday}
              </span>
            ))}
          </div>

          <div
            className="PeriodPicker__grid"
            onMouseLeave={() => onDayHover(null)}
          >
            {grid.map((day, index) => {
              if (!day) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="PeriodPicker__cell PeriodPicker__cell--empty"
                  />
                );
              }

              const modifier = getDayModifier(day);

              return (
                <div
                  key={day.valueOf()}
                  className={cn('PeriodPicker__cell', {
                    [`PeriodPicker__cell--${modifier}`]: Boolean(modifier),
                    'PeriodPicker__cell--today': isToday(day),
                  })}
                  onMouseEnter={() => onDayHover(day)}
                >
                  <button
                    type="button"
                    className="PeriodPicker__cell-btn"
                    onClick={() => onDayClick(day)}
                  >
                    {day.date()}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {aside && (
          <>
            <div className="PeriodPicker__divider" />
            <div className="PeriodPicker__aside">{aside}</div>
          </>
        )}
      </div>

      {hint && <div className="PeriodPicker__hint">{hint}</div>}

      {(onReset || onSave) && (
        <div className="PeriodPicker__footer">
          {onReset && (
            <button
              type="button"
              className="PeriodPicker__reset"
              onClick={onReset}
            >
              Сбросить период
            </button>
          )}
          {onSave && (
            <Button
              className="PeriodPicker__save"
              type="primary"
              size="small"
              disabled={isSaveDisabled}
              onClick={onSave}
            >
              Сохранить
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
